import { ERRORS, lookupErrorMessage } from '@rynora/messages';
import { getApiBaseUrl } from './config';

const API_BASE = getApiBaseUrl();
const TOKEN_KEY = 'rynora_token';
const USER_KEY = 'rynora_session';

function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

function cacheUser(user: any) {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

function getCachedUser(): any | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (!res.ok) {
    // Backend canonical shape: { code, message, detail? }. Older routes still
    // ship { error: '...' } — `lookupErrorMessage` reads both.
    const body = await res.json().catch(() => ({ message: res.statusText }));
    const code = typeof body?.code === 'string' ? body.code : undefined;
    const message = lookupErrorMessage({ code, message: body?.message ?? body?.error }) || ERRORS.UNKNOWN;
    throw new ApiError(res.status, message, code);
  }
  return res.json();
}

export const api = {
  auth: {
    async register(name: string, email: string, password: string) {
      const data = await request<{ user: any; token: string }>('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify({ name, email, password }),
      });
      setToken(data.token);
      cacheUser(data.user);
      return data;
    },

    async login(email: string, password: string) {
      const data = await request<{ user: any; token: string }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      setToken(data.token);
      cacheUser(data.user);
      return data;
    },

    async me() {
      const user = await request<any>('/api/auth/me');
      cacheUser(user);
      return user;
    },

    async forgotPassword(email: string) {
      return request<{ message: string; _demo_token?: string }>('/api/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
    },

    async resetPassword(token: string, newPassword: string) {
      return request<{ message: string }>('/api/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token, newPassword }),
      });
    },

    logout() {
      clearToken();
    },

    isAuthenticated() {
      return !!getToken();
    },

    getCachedUser,
  },
};
